import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, Receipt } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import {
  useDecideFineDispute,
  useGetDrivingFines,
  useOfferFineConsent,
  useRecordFineConsent,
  useRecordFineDispute,
  useRecordFineNotice,
} from '../../hooks/backend/drivers'
import { useSetFineOffence } from '../../hooks/backend/fines'
import { writeErrorMessage } from '../../utils/writeError'
import { readableDate } from '../../utils/coachingDates'
import { NOTICE_METHODS, dayLabel } from '../../utils/drivingConduct'
import {
  BUCKETS,
  CATEGORY_OPTIONS,
  CONSENT_LABEL,
  DISPUTE_KIND_LABEL,
  OUTCOME_LABEL,
  fineSpeedLine,
  fineStanding,
  rand,
  type DrivingFinesPage,
  type FineBucket,
  type FineRow,
  type UnclassifiedRow,
} from '../../utils/drivingFines'

// Speeding fines on the driving conduct ladder: clause 21.6A of the v6
// agreement, Addendum No. 1 clause 5.1A (db/migrations/0390 and 0480 to 0484).
//
// THIS TAB DECIDES NOTHING. Which bucket a fine is in, whether it is reckless,
// whether it counts, the dispute deadline and who may act all come from
// driving_conduct_fines_v1. A Driver Servant records the steps here: the notice
// given by hand, a dispute that reached them some other way, the outcome of a
// dispute, and the clause 14.2(f) consent. The ladder step itself is raised by
// the daily detector and handled on the Events and cases view, like any other
// driving conduct case.
//
// THE MONEY IS SEPARATE. Offering consent, and the driver's answer, never touch
// a conduct case, and a case never touches the money.

type Form =
  | { kind: 'notice'; fine: FineRow }
  | { kind: 'dispute'; fine: FineRow }
  | { kind: 'decide'; fine: FineRow }
  | { kind: 'consent'; fine: FineRow }
  | { kind: 'offence'; fineId: string; label: string }

function todayIso(): string {
  return new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 10)
}

const RECKLESS_BEFORE_CONFIRMED: FineBucket[] = ['awaiting_attribution', 'awaiting_response', 'disputed']

export function DrivingFinesPanel() {
  const page = useGetDrivingFines<DrivingFinesPage>()
  const notice = useRecordFineNotice()
  const dispute = useRecordFineDispute()
  const decide = useDecideFineDispute()
  const offer = useOfferFineConsent()
  const consent = useRecordFineConsent()
  const offence = useSetFineOffence()
  const [bucket, setBucket] = useState<FineBucket | null>(null)
  const [form, setForm] = useState<Form | null>(null)
  const [showUnclassified, setShowUnclassified] = useState(false)
  const [busy, setBusy] = useState(false)
  // One set of inputs, cleared whenever a form opens.
  const [date, setDate] = useState(todayIso())
  const [choice, setChoice] = useState('')
  const [text, setText] = useState('')
  const [extra, setExtra] = useState('')
  const [recorded, setRecorded] = useState('')
  const [limit, setLimit] = useState('')

  const load = page.trigger
  const refresh = useCallback(() => load({}, { skipCache: true }), [load])
  useEffect(() => {
    refresh()
  }, [refresh])

  const d = page.data
  const active: FineBucket = bucket ?? (d ? (BUCKETS.find((b) => (d.counts[b.key] ?? 0) > 0)?.key ?? 'awaiting_response') : 'awaiting_response')
  const rows = useMemo(() => {
    const all = d?.fines ?? []
    if (active === 'reckless') return all.filter((f) => f.bucket === 'reckless' || (f.reckless && RECKLESS_BEFORE_CONFIRMED.includes(f.bucket)))
    return all.filter((f) => f.bucket === active)
  }, [d, active])

  function open(next: Form) {
    setForm(next)
    setDate(todayIso())
    setChoice('')
    setText('')
    setExtra('')
    setRecorded('')
    setLimit('')
  }

  async function run(what: string, fn: () => Promise<unknown>) {
    setBusy(true)
    try {
      await fn()
      toast.success(what)
      setForm(null)
      await refresh()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (page.loading && !d) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }
  if (page.error && !d) {
    return (
      <p className="m-4 flex items-start gap-1.5 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          The Fines tab could not be read. The server said: <span className="font-medium">{page.error}</span>
        </span>
      </p>
    )
  }
  if (!d) return null

  return (
    <div className="space-y-5 px-4 py-4 text-sm">
      <section className="space-y-1 text-xs text-muted-foreground">
        <p>
          A speeding fine counts as a breach week (clause 21.6A) only once Fleetly has established, from the shift and allocation
          records, that the driver was driving, and only after he has had {d.dispute_working_days} working days to dispute it. A fine for
          more than {d.reckless_over_limit_kmh} km/h over the limit, or above {d.reckless_absolute_kmh} km/h, is reckless driving (clause
          21.5) and goes to a hearing. A fine and a Cartrack breach week in the same 7 days are one breach week.
        </p>
        <p>
          {d.messages_on
            ? 'Notices and consent offers are emailed to drivers when a fine is attributed.'
            : 'Emails to drivers are switched off until the induction at which Addendum No. 1 is signed. Give the notice in writing yourself and record it here; the dispute window starts from that date.'}
        </p>
      </section>

      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Speeding fines by stage">
        {BUCKETS.map((b) => (
          <button
            key={b.key}
            type="button"
            role="tab"
            aria-selected={active === b.key}
            onClick={() => {
              setBucket(b.key)
              setForm(null)
            }}
            className={
              'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ' +
              (active === b.key
                ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))] text-white'
                : 'border-border text-muted-foreground hover:text-foreground')
            }
          >
            {b.label} <span className="tabular-nums">({d.counts[b.key] ?? 0})</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{BUCKETS.find((b) => b.key === active)?.blurb}</p>

      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
          No speeding fines here.
          {d.counts.unclassified > 0 &&
            ` ${d.counts.unclassified} traffic notice(s) have not been classified, so none of them can be told apart as speeding yet. See the list below.`}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5">When</th>
                <th className="px-2 py-1.5">Car</th>
                <th className="px-2 py-1.5">Driver</th>
                <th className="px-2 py-1.5">Speed</th>
                <th className="px-2 py-1.5">Amount</th>
                <th className="px-2 py-1.5">Where it stands</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <FineLine key={f.fine_id} f={f} canClassify={d.can_classify} onOpen={open} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && form.kind !== 'offence' && (
        <fieldset className="space-y-2 rounded-md border border-border p-3" disabled={busy}>
          <legend className="px-1 text-xs font-semibold">
            {form.kind === 'notice' && 'Record the written notice'}
            {form.kind === 'dispute' && 'Record a dispute the driver made'}
            {form.kind === 'decide' && 'Record the outcome of the dispute'}
            {form.kind === 'consent' && (form.fine.consent?.status === 'offered' ? 'Record his answer to the consent' : 'Offer the clause 14.2(f) consent')}
            {': '}
            {form.fine.driver_name ?? 'driver'}, notice {form.fine.notice_number ?? 'without a number'}
          </legend>

          {form.kind === 'notice' && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div>
                <Label htmlFor="fn-date">Date given</Label>
                <Input id="fn-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="fn-method">How, in writing</Label>
                <NativeSelect id="fn-method" value={choice} onChange={(e) => setChoice(e.target.value)}>
                  <option value="">Choose</option>
                  {NOTICE_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              <div>
                <Label htmlFor="fn-note">Note (optional)</Label>
                <Input id="fn-note" value={text} onChange={(e) => setText(e.target.value)} />
              </div>
            </div>
          )}

          {form.kind === 'dispute' && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div>
                <Label htmlFor="fd-kind">What he disputes</Label>
                <NativeSelect id="fd-kind" value={choice} onChange={(e) => setChoice(e.target.value)}>
                  <option value="">Choose</option>
                  <option value="not_the_driver">{DISPUTE_KIND_LABEL.not_the_driver}</option>
                  <option value="the_fine">{DISPUTE_KIND_LABEL.the_fine}</option>
                </NativeSelect>
              </div>
              <div>
                <Label htmlFor="fd-date">Date he disputed it</Label>
                <Input id="fd-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="sm:col-span-3">
                <Label htmlFor="fd-text">What he said</Label>
                <Input id="fd-text" value={text} onChange={(e) => setText(e.target.value)} />
              </div>
            </div>
          )}

          {form.kind === 'decide' && (
            <>
              <p className="text-xs">
                <span className="font-medium">{DISPUTE_KIND_LABEL[form.fine.dispute_kind ?? 'the_fine']}:</span> {form.fine.dispute_text}
                {form.fine.disputed_on && <span className="text-muted-foreground"> ({readableDate(form.fine.disputed_on)})</span>}
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div>
                  <Label htmlFor="fo-out">Outcome</Label>
                  <NativeSelect id="fo-out" value={choice} onChange={(e) => setChoice(e.target.value)}>
                    <option value="">Choose</option>
                    <option value="dispute_rejected">Rejected: he was the driver and the fine stands</option>
                    <option value="upheld_not_driver">Upheld: he was not the driver</option>
                    <option value="upheld_fine">Upheld: the fine itself is wrong</option>
                  </NativeSelect>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="fo-why">Reasons</Label>
                  <Input id="fo-why" value={text} onChange={(e) => setText(e.target.value)} />
                </div>
              </div>
            </>
          )}

          {form.kind === 'consent' && form.fine.consent?.status === 'offered' && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
              <div>
                <Label htmlFor="fc-dec">His answer</Label>
                <NativeSelect id="fc-dec" value={choice} onChange={(e) => setChoice(e.target.value)}>
                  <option value="">Choose</option>
                  <option value="signed">Signed the consent</option>
                  <option value="refused">Refused</option>
                  <option value="withdrawn">Offer withdrawn (made in error)</option>
                </NativeSelect>
              </div>
              <div>
                <Label htmlFor="fc-date">Date</Label>
                <Input id="fc-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="fc-ev">Where the signed copy is kept</Label>
                <Input id="fc-ev" placeholder="Driver file, or a SignNow envelope" value={text} onChange={(e) => setText(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="fc-note">Note (needed if withdrawn)</Label>
                <Input id="fc-note" value={extra} onChange={(e) => setExtra(e.target.value)} />
              </div>
            </div>
          )}
          {form.kind === 'consent' && form.fine.consent?.status !== 'offered' && (
            <p className="text-xs text-muted-foreground">
              He may consent to {rand(form.fine.amount_zar)} being recovered from his pay for this fine, or refuse. If he signs, payroll
              recovers it within the section 34 limit. If he refuses, it is recorded as owing and nothing is taken. Either way it has no
              bearing on any conduct step.
            </p>
          )}

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                const id = form.fine.fine_id
                if (form.kind === 'notice')
                  run('Notice recorded', () => notice.trigger({ fine_id: id, given_on: date, method: choice, note: text || null }))
                if (form.kind === 'dispute')
                  run('Dispute recorded', () => dispute.trigger({ fine_id: id, kind: choice, text, received_on: date }))
                if (form.kind === 'decide') run('Outcome recorded', () => decide.trigger({ fine_id: id, outcome: choice, reasons: text }))
                if (form.kind === 'consent' && form.fine.consent?.status === 'offered')
                  run('Answer recorded', () =>
                    consent.trigger({
                      fine_id: id,
                      decision: choice,
                      decided_on: date,
                      evidence: text || null,
                      signnow_envelope_id: null,
                      note: extra || null,
                    }),
                  )
                else if (form.kind === 'consent') run('Consent offered', () => offer.trigger({ fine_id: id, note: null }))
              }}
            >
              {form.kind === 'consent' && form.fine.consent?.status !== 'offered' ? 'Offer consent' : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setForm(null)}>
              Cancel
            </Button>
          </div>
        </fieldset>
      )}

      {form && form.kind === 'offence' && (
        <fieldset className="space-y-2 rounded-md border border-border p-3" disabled={busy}>
          <legend className="px-1 text-xs font-semibold">What the notice was for: {form.label}</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
            <div>
              <Label htmlFor="of-cat">Offence</Label>
              <NativeSelect id="of-cat" value={choice} onChange={(e) => setChoice(e.target.value)}>
                <option value="">Choose</option>
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div>
              <Label htmlFor="of-rec">Recorded km/h</Label>
              <Input id="of-rec" inputMode="numeric" disabled={choice !== 'speeding'} value={recorded} onChange={(e) => setRecorded(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="of-lim">Limit km/h</Label>
              <Input id="of-lim" inputMode="numeric" disabled={choice !== 'speeding'} value={limit} onChange={(e) => setLimit(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="of-note">Note (needed once the driver was told)</Label>
              <Input id="of-note" value={text} onChange={(e) => setText(e.target.value)} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() =>
                run('Offence recorded', () =>
                  offence.trigger({
                    id: form.fineId,
                    offence_category: choice || null,
                    recorded_kmh: choice === 'speeding' ? recorded || null : null,
                    limit_kmh: choice === 'speeding' ? limit || null : null,
                    note: text || null,
                  }),
                )
              }
            >
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setForm(null)}>
              Cancel
            </Button>
          </div>
        </fieldset>
      )}

      <section>
        <button
          type="button"
          className="flex items-center gap-1 text-sm font-semibold"
          onClick={() => setShowUnclassified((v) => !v)}
          aria-expanded={showUnclassified}
        >
          {showUnclassified ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Receipt className="h-4 w-4" /> Traffic notices not yet classified ({d.counts.unclassified})
        </button>
        <p className="mt-1 text-xs text-muted-foreground">
          The rule reads the offence description. These rows have none it can read, so a person has to say from the notice what each
          was for. The history loaded from the old sheet and the owner statements never recorded it.
          {!d.can_classify && ' Classifying is for those who manage traffic fines.'}
        </p>
        {showUnclassified && (
          <div className="mt-2 max-h-96 overflow-auto rounded-md border border-border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted text-left text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5">Date</th>
                  <th className="px-2 py-1.5">Car</th>
                  <th className="px-2 py-1.5">Notice</th>
                  <th className="px-2 py-1.5">Amount</th>
                  <th className="px-2 py-1.5">Source</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {d.unclassified.map((u) => (
                  <UnclassifiedLine
                    key={u.fine_id}
                    u={u}
                    canClassify={d.can_classify}
                    onClassify={() => open({ kind: 'offence', fineId: u.fine_id, label: `${u.plate ?? 'no car'}, ${rand(u.amount_zar)}` })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function FineLine({ f, canClassify, onOpen }: { f: FineRow; canClassify: boolean; onOpen: (form: Form) => void }) {
  const action: { label: string; form: Form } | null = (() => {
    if (f.bucket === 'awaiting_attribution' && canClassify && f.unattributed_reason === 'speeds_not_entered')
      return { label: 'Enter speeds', form: { kind: 'offence', fineId: f.fine_id, label: `${f.plate ?? ''}, notice ${f.notice_number ?? ''}` } }
    if (!f.can_act) return null
    if (f.stage === 'notice_pending') return { label: 'Record notice', form: { kind: 'notice', fine: f } }
    if (f.stage === 'awaiting_response') return { label: 'Record dispute', form: { kind: 'dispute', fine: f } }
    if (f.stage === 'disputed') return { label: 'Record outcome', form: { kind: 'decide', fine: f } }
    if ((f.bucket === 'counted' || f.bucket === 'reckless') && f.gate_date && (!f.consent || f.consent.status === 'offered' || f.consent.status === 'withdrawn'))
      return { label: f.consent?.status === 'offered' ? 'Record consent answer' : 'Offer consent', form: { kind: 'consent', fine: f } }
    return null
  })()

  return (
    <tr className="border-t border-border align-top">
      <td className="whitespace-nowrap px-2 py-1.5">
        {f.offence_on ? `${dayLabel(f.offence_on)} ${f.offence_time ?? ''}` : '—'}
        <div className="text-[11px] text-muted-foreground">{f.notice_number ?? 'no notice number'}</div>
      </td>
      <td className="whitespace-nowrap px-2 py-1.5">
        {f.plate ?? '—'}
        <div className="text-[11px] text-muted-foreground">{f.place ?? 'place not given'}</div>
      </td>
      <td className="px-2 py-1.5">
        {f.driver_name ?? f.confirmed_driver_name ?? <span className="text-muted-foreground">not confirmed</span>}
        {f.manager_name && <div className="text-[11px] text-muted-foreground">{f.manager_name}</div>}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5">
        {fineSpeedLine(f)}
        {f.reckless && (
          <Chip tone="red" className="ml-1">
            Reckless
          </Chip>
        )}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 tabular-nums">{rand(f.amount_zar)}</td>
      <td className="px-2 py-1.5">
        {fineStanding(f)}
        {f.outcome && f.bucket !== 'not_counted' && <div className="text-[11px] text-muted-foreground">{OUTCOME_LABEL[f.outcome]}</div>}
        {f.consent && <div className="text-[11px] text-muted-foreground">{CONSENT_LABEL[f.consent.status]}</div>}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 text-right">
        {action && (
          <Button size="sm" variant="outline" onClick={() => onOpen(action.form)}>
            {action.label}
          </Button>
        )}
      </td>
    </tr>
  )
}

function UnclassifiedLine({ u, canClassify, onClassify }: { u: UnclassifiedRow; canClassify: boolean; onClassify: () => void }) {
  const when = u.offence_at ? dayLabel(u.offence_at.slice(0, 10)) : u.sheet_date ? `${readableDate(u.sheet_date)} (sheet)` : u.statement_period ? `${u.statement_period.slice(0, 7)} (statement month)` : '—'
  return (
    <tr className="border-t border-border">
      <td className="whitespace-nowrap px-2 py-1.5">{when}</td>
      <td className="whitespace-nowrap px-2 py-1.5">{u.plate ?? '—'}</td>
      <td className="px-2 py-1.5">{u.notice_number ?? '—'}</td>
      <td className="whitespace-nowrap px-2 py-1.5 tabular-nums">{rand(u.amount_zar)}</td>
      <td className="px-2 py-1.5">{u.source === 'manual' ? 'Captured notice' : u.source === 'legacy_sheet' ? 'Old fines sheet' : 'Owner statement'}</td>
      <td className="px-2 py-1.5 text-right">
        {canClassify && (
          <Button size="sm" variant="ghost" onClick={onClassify}>
            Classify
          </Button>
        )}
      </td>
    </tr>
  )
}
